import { Course } from '../models/course';
import { ELearning, ELearningDetail } from '../models/e-learning';
import { Learner, LearnerWithInstructorNotes } from '../models/learner';
import { User } from '../models/user';
import { activeMapper } from './active.mapper';
import { courseMapper } from './course.mapper';
import { dateMapper } from './date.mapper';
import { managerRequestMapper } from './group-admin-request.mapper';
import { licenseStatusMapper } from './license-status.mapper';
import { orderSpecialRequest } from './order-special-request.mapper';
import { packageMapper } from './package.mapper';
import { paymentItemMapper } from './payment-item.mapper';
import { userMapper } from './user.mapper';

export const E_LEARNING_FIELDS = {
  id: 'id',
  createdDate: 'create_date',
  'user.id': 'user_id',
  confirmationId: 'confirmation_id',
  expiredDate: 'product_registration_expiration_date',
};

/**
 * Map to eLearning.
 * @param {object} dto Dto.
 * @returns {ELearning}
 */
function fromDto(dto) {
  return new ELearning({
    id: dto.id,
    confirmationId: dto.confirmation_id,
    status: dto?.status ? licenseStatusMapper.fromDto(dto.status) : null,
    allocationDate: dto.allocation_date != null ? dateMapper.fromDto(dto.allocation_date) : null,
    userId: dto.user_id,
    registerUserId: dto.register_user_id,
    source: dto.source,
    examScore: dto.exam_score,
    certificateNumber: dto.certificate_number,
    certSendDate: dateMapper.fromDto(dto.cert_send_date),
    createdDate: dateMapper.fromDto(dto.create_date),
    isNewSystem: dto.is_new_system,
    editedDate: dateMapper.fromDto(dto.edit_date),
    isActive: activeMapper.fromDto(dto.active),
    user: dto?.user ? userMapper.fromDto(dto.user) : null,
    registerUser: dto?.register_user ? userMapper.fromDto(dto.register_user) : null,
    orderId: dto.order_id,
    balance: dto.balance,
    package: dto.package ? packageMapper.fromDto(dto.package) : null,
    courseInfo: dto.course_info ? courseMapper.fromDto(dto.course_info) : null,
    language: dto.language,
    isVisible: dto.visible,
    studentAccessPage: dto.student_access_page,
    registrationSource: dto.source,

    // Update after BE update swagger
    orderItem: dto.order_item
      ? {
        createdDate: dto.order_item.create_date,
        order: {
          id: dto.order_item.order?.id,
        },
      }
      : null,

  });
}

/**
 * Map to eLearning detail.
 * @param {object} dto Dto.
 * @returns {ELearningDetail}
 */
function fromDetailDto(dto) {
  return new ELearningDetail({
    ...fromDto(dto),
    payment: dto.payment != null ? paymentItemMapper.fromDto(dto.payment) : null,
    refundPayment: dto.refund_payment ? paymentItemMapper.refundPaymentFromDto(dto.refund_payment) : null,
    orderSpecialRequests: dto.order_special_requests ? dto.order_special_requests.map(order => orderSpecialRequest.fromDto(order)) : [],
    hasPreBoughtLaminatedCard: dto.has_pre_bought_laminated_card ? dto.has_pre_bought_laminated_card : null,
    laminatedCardPackage: dto.laminated_card_package ? packageMapper.fromDto(dto.laminated_card_package) : null,
  });
}

/**
 * Map downloaded certificates from dto.
 * @param {object} certificatesObject Certificates object.
 * @returns {string[]}
 */
function mapDownloadedCertificatesFromDto(certificatesObject) {
  return Object.values(certificatesObject);
}

/**
 * Map assign courses data to dto
 * @param {object} data Assign courses data.
 * @param {LearnerWithInstructorNotes[] | Learner[]} data.learners Learners.
 * @param {User} data.groupAdmin Group admin.
 * @param {number} data.divisionId Division ID.
 * @returns {object}
 */
function toAssignCoursesDto({ learners, groupAdmin, divisionId }) {
  return {
    learners: learners.map(learner => ({
      user_id: learner.id,
      instructor_note: learner.instructorNotes ?? null,
    })),
    ...managerRequestMapper.toDto({ groupAdmin }),
    division_id: divisionId,
  };
}

/**
 * Map assign courses data to dto
 * @param {object} data Assign courses data.
 * @param {User} data.groupAdmin Group admin.
 * @param {number[]} data.learnerIds Learner IDs.
 * @returns {object}
 */
function toAssignCoursesWithLearnerIdsDto({ learnerIds, groupAdmin }) {
  return {
    learners: learnerIds.map(id => ({
      user_id: id,
    })),
    ...managerRequestMapper.toDto({ groupAdmin }),
  };
}

/**
 * Map data to restart training dto.
 * @param {ELearning} eLearning E-learning.
 * @returns {object}
 */
function toRestartTrainingDto(eLearning) {
  return {
    registration_id: eLearning.id,
    course_id: eLearning.courseInfo.course.id,
  };
}

/**
 * Map data to email certificate dto.
 * @param {number} registrationId Registration id.
 * @returns {object}
 */
function toEmailCertificateDto(registrationId) {
  return {
    registration_ids: [registrationId],
  };
}

/**
 * Map data to registration ids dto.
 * @param {number[]} registrationIds Registration id.
 * @param {object} userInfo User info.
 * @returns {object}
 */
function toRegistrationIdsDto(registrationIds, userInfo = null) {
  return {
    registration_ids: registrationIds,
    user_info: userInfo,
  };
}

/**
 * Map data to include laminated card info flag.
 * @param {boolean} isIncludeCard Is included.
 * @returns {object}
 */
function toIncludeLaminatedCardIncludeInfoDto(isIncludeCard) {
  return {
    include_laminated_card_info: isIncludeCard ? 1 : 0,
  };
}

/**
 * Map data to expiration date dto.
 * @param {object} settings Settings.
 * @returns {object}
 */
function toExpirationDateDto(settings) {
  return {
    status_id: settings.status.id,
    expiration_date: dateMapper.toDto(settings.expirationDate),
  };
}

/**
 * Map data to general settings dto.
 * @param {ELearning | undefined} eLearning E-learning.
 * @param {object} settings Settings.
 * @returns {object}
 */
function toGeneralSettingsDto(eLearning, settings) {
  return {
    ...toExpirationDateDto(settings),
    training_certificate_id: eLearning.courseInfo.trainingCertificate.id,
    create_date: dateMapper.toDto(settings.issueDate),
    code: settings.verificationCode,
    source_id: settings.registrationSource.id,
    certificate_expiration_date: dateMapper.toDto(settings.certificateSendDate),
  };
}

/**
 * Map data to progress dto.
 * @param {number} progress Progress.
 * @returns {object}
 */
function toUpdateProgressDto(progress) {
  return {
    slide_number: progress,
  };
}

/**
 * Map data to transfer course dto.
 * @param {Learner} learner Learner.
 * @param {Course} course Course.
 * @returns {object}
 */
function toTransferCourseDto(learner, course) {
  return {
    course_id: course.course.id,
    user_id: learner.id,
  };
}

export const eLearningMapper = {
  fromDto,
  fromDetailDto,
  mapDownloadedCertificatesFromDto,
  toAssignCoursesDto,
  toRestartTrainingDto,
  toEmailCertificateDto,
  toRegistrationIdsDto,
  toIncludeLaminatedCardIncludeInfoDto,
  toGeneralSettingsDto,
  toUpdateProgressDto,
  toTransferCourseDto,
  toExpirationDateDto,
  toAssignCoursesWithLearnerIdsDto,
};
